import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Book, Dumbbell, Code, MapPin, Music, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTutors } from '@/hooks/useTutors';
import FeaturedTutorsModal from './FeaturedTutorsModal';
import { getProfilePictureUrl } from '@/utils/profileUtils';

interface SubjectsSidebarProps {
  onCollapseChange?: (collapsed: boolean) => void;
}

const SubjectsSidebar = ({ onCollapseChange }: SubjectsSidebarProps = {}) => {
  const navigate = useNavigate();
  const { tutors, isLoading } = useTutors();
  const [showMobileNav, setShowMobileNav] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showFeaturedModal, setShowFeaturedModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolling, setIsScrolling] = useState(false);

  // Filter featured tutors
  const featuredTutors = tutors.filter(tutor => tutor.isFeatured === true);

  // Improved scroll handler for mobile navigation with throttling
  useEffect(() => {
    let ticking = false;
    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const scrollDifference = Math.abs(currentScrollY - lastScrollY);
          
          // Only update if scroll difference is significant (reduces jitter)
          if (scrollDifference > 5) {
            setIsScrolling(true);
            
            // Clear existing timeout
            clearTimeout(scrollTimeout);
            
            // Set scrolling to false after scroll stops
            scrollTimeout = setTimeout(() => {
              setIsScrolling(false);
            }, 150);

            if (currentScrollY < 100) {
              // Always show at top
              setShowMobileNav(true);
            } else if (currentScrollY > lastScrollY + 10) {
              // Hide when scrolling down (with threshold)
              setShowMobileNav(false);
            } else if (lastScrollY > currentScrollY + 10) {
              // Show when scrolling up (with threshold)
              setShowMobileNav(true);
            }
            
            setLastScrollY(currentScrollY);
          }
          
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [lastScrollY]);

  const focusSubjects = [
    {
      id: 'english',
      name: 'English',
      description: 'Master grammar, writing, and literature',
      icon: 'Book',
      route: '/subjects/english',
      iconBgColor: 'bg-blue-600',
      buttonColor: 'bg-blue-600 hover:bg-blue-700',
      gradientFrom: 'from-blue-400',
      gradientTo: 'to-blue-600'
    },
    {
      id: 'fitness',
      name: 'Fitness',
      description: 'Get in shape with personalized coaching',
      icon: 'Dumbbell',
      route: '/subjects/fitness',
      iconBgColor: 'bg-green-600',
      buttonColor: 'bg-green-600 hover:bg-green-700',
      gradientFrom: 'from-green-400',
      gradientTo: 'to-green-600'
    },
    {
      id: 'programming',
      name: 'Programming',
      description: 'Learn coding from industry experts',
      icon: 'Code',
      route: '/subjects/programming',
      iconBgColor: 'bg-purple-600',
      buttonColor: 'bg-purple-600 hover:bg-purple-700',
      gradientFrom: 'from-purple-400',
      gradientTo: 'to-purple-600'
    },
    {
      id: 'music',
      name: 'Music',
      description: 'Learn instruments and theory',
      icon: 'Music',
      route: '/subjects/music',
      iconBgColor: 'bg-orange-600',
      buttonColor: 'bg-orange-600 hover:bg-orange-700',
      gradientFrom: 'from-orange-400',
      gradientTo: 'to-orange-600'
    },
    {
      id: 'travel',
      name: 'Travel',
      description: 'Explore with experienced guides',
      icon: 'MapPin',
      route: '/subjects/travel',
      iconBgColor: 'bg-teal-600',
      buttonColor: 'bg-teal-600 hover:bg-teal-700',
      gradientFrom: 'from-teal-400',
      gradientTo: 'to-teal-600'
    }
  ];

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'Book': return Book;
      case 'Dumbbell': return Dumbbell;
      case 'Code': return Code;
      case 'Music': return Music;
      case 'MapPin': return MapPin;
      default: return Book;
    }
  };

  const handleSubjectClick = (subject: any) => {
    navigate(subject.route);
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      // Check if the search query matches any subject
      const matchingSubject = focusSubjects.find(subject => 
        subject.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        subject.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      
      if (matchingSubject) {
        navigate(matchingSubject.route);
      } else {
        // If no subject match, search for tutors
        const params = new URLSearchParams();
        params.append('search', searchQuery);
        navigate(`/tutors?${params.toString()}`);
      }
    } else {
      navigate('/subjects');
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getTutorName = (tutor: any) => {
    if (tutor.name && tutor.name.trim()) {
      return tutor.name;
    }
    if (tutor.expand?.user?.name) {
      return tutor.expand.user.name;
    }
    return 'Featured Tutor';
  };

  return (
    <>
      {/* Desktop Sidebar - Always visible, no collapse */}

      
      <div className={`hidden lg:block fixed left-0 top-16 w-[20%] h-[calc(100vh-4rem)] bg-gray-50 overflow-y-auto z-10`}>
        <div className="p-6 space-y-3 h-full flex flex-col">
          {/* Header */}
          <div className="text-center mb-4 flex-shrink-0">
            <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">Our Focus Subjects</h2>
            <p className="text-xs text-gray-600">Choose your learning path</p>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto mt-2 rounded-full"></div>
          </div>

          {/* Subjects - Responsive to screen height */}
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 flex flex-col gap-2 min-h-0">
              {focusSubjects.map((subject, index) => {
              const IconComponent = getIconComponent(subject.icon || 'Book');
              
              return (
                <div
                  key={subject.id} 
                  className="animate-fade-in flex-1 min-h-0"
                  style={{animationDelay: `${index * 0.1}s`}}
                >
                  <div 
                    className={`group cursor-pointer p-1 lg:p-1.5 xl:p-2 2xl:p-3 h-full overflow-hidden ${
                      subject.id === 'english' ? 'bg-blue-50 hover:bg-blue-100 border-blue-200' :
                      subject.id === 'fitness' ? 'bg-green-50 hover:bg-green-100 border-green-200' :
                      subject.id === 'programming' ? 'bg-purple-50 hover:bg-purple-100 border-purple-200' :
                      subject.id === 'music' ? 'bg-orange-50 hover:bg-orange-100 border-orange-200' :
                      'bg-teal-50 hover:bg-teal-100 border-teal-200'
                    } border rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105`}
                    onClick={() => handleSubjectClick(subject)}
                  >
                    <div className="flex items-center space-x-1 lg:space-x-1.5 xl:space-x-2 2xl:space-x-3 overflow-hidden">
                      <div className={`p-0.5 lg:p-1 xl:p-1.5 2xl:p-2 flex-shrink-0 ${
                        subject.id === 'english' ? 'bg-blue-100 group-hover:bg-blue-200 border-blue-200' :
                        subject.id === 'fitness' ? 'bg-green-100 group-hover:bg-green-200 border-green-200' :
                        subject.id === 'programming' ? 'bg-purple-100 group-hover:bg-purple-200 border-purple-200' :
                        subject.id === 'music' ? 'bg-orange-100 group-hover:bg-orange-200 border-orange-200' :
                        'bg-teal-100 group-hover:bg-teal-200 border-teal-200'
                      } rounded-lg transition-colors border`}>
                        <IconComponent className={`h-2 w-2 lg:h-2.5 lg:w-2.5 xl:h-3 xl:w-3 2xl:h-4 2xl:w-4 ${
                          subject.id === 'english' ? 'text-blue-600' :
                          subject.id === 'fitness' ? 'text-green-600' :
                          subject.id === 'programming' ? 'text-purple-600' :
                          subject.id === 'music' ? 'text-orange-600' :
                          'text-teal-600'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <h3 className="font-medium text-gray-900 text-[9px] lg:text-[10px] xl:text-xs 2xl:text-sm mb-0 xl:mb-0.5 2xl:mb-1 truncate">
                          {subject.name}
                        </h3>
                        <p 
                          className="text-gray-500 text-[7px] lg:text-[8px] xl:text-[10px] 2xl:text-xs truncate leading-tight"
                          title={subject.description}
                        >
                          {subject.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          </div>

          {/* Search Field */}
          <div className="mb-4 flex-shrink-0">
            <div className="relative flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search subjects"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  className="pl-8 pr-2 py-1.5 bg-white border-gray-200 focus:border-blue-300 focus:ring-blue-200 rounded-lg text-xs placeholder:text-gray-400"
                />
              </div>
              <Button
                onClick={handleSearch}
                size="sm"
                variant="outline"
                className="bg-white hover:bg-gray-50 text-black border-gray-200 font-medium px-3 rounded-lg transition-all duration-200 text-xs flex-shrink-0"
              >
                <Search className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {/* Featured Tutors Section */}
          <div className="flex-shrink-0">
            <div 
              className="cursor-pointer p-3 bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
              onClick={() => setShowFeaturedModal(true)}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-900">Featured Tutors</h3>
              </div>
              
              {/* Featured Tutor Photos Preview */}
              <div className="flex gap-1.5 mb-2">
                {featuredTutors.slice(0, 4).map((tutor, index) => (
                  <div key={tutor.id} className="relative">
                    <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-white shadow-sm">
                      <img 
                        src={getProfilePictureUrl(tutor) || `https://images.unsplash.com/photo-${
                          index === 0 ? '1649972904349-6e44c42644a7' :
                          index === 1 ? '1581091226825-a6a2a5aee158' :
                          index === 2 ? '1581092795360-fd1ca04f0952' :
                          '1535713875002-d1d0cf377fde'
                        }?auto=format&fit=crop&w=100&q=80`}
                        alt={getTutorName(tutor)}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `https://images.unsplash.com/photo-${
                            index === 0 ? '1649972904349-6e44c42644a7' :
                            index === 1 ? '1581091226825-a6a2a5aee158' :
                            index === 2 ? '1581092795360-fd1ca04f0952' :
                            '1535713875002-d1d0cf377fde'
                          }?auto=format&fit=crop&w=100&q=80`;
                        }}
                      />
                    </div>
                    {index === 3 && featuredTutors.length > 4 && (
                      <div className="absolute inset-0 bg-black bg-opacity-60 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-semibold">+{featuredTutors.length - 4}</span>
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Show placeholder if less than 4 tutors */}
                {featuredTutors.length < 4 && Array.from({ length: 4 - featuredTutors.length }).map((_, index) => (
                  <div key={`placeholder-${index}`} className="w-7 h-7 rounded-full bg-gray-200 border-2 border-white shadow-sm flex items-center justify-center">
                    <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-gray-500">
                {featuredTutors.length > 0 
                  ? `${featuredTutors.length} expert${featuredTutors.length === 1 ? '' : 's'} available`
                  : 'No featured tutors yet'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      <FeaturedTutorsModal 
        open={showFeaturedModal}
        onOpenChange={setShowFeaturedModal}
        tutors={featuredTutors}
        isLoading={isLoading}
      />

      {/* Mobile Bottom Navigation - Show on scroll with improved transitions */}
      <div className={`fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-lg z-30 lg:hidden transition-all duration-500 ease-in-out ${
        showMobileNav ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      } ${isScrolling ? 'transition-duration-200' : 'transition-duration-500'}`}>
        <div className="flex justify-around items-center py-2 px-1">
          {focusSubjects.map((subject, index) => {
            const IconComponent = getIconComponent(subject.icon || 'Book');
            
            return (
              <div
                key={subject.id}
                className="flex-1 animate-fade-in"
                style={{animationDelay: `${index * 0.1}s`}}
              >
                <button
                  className="w-full py-3 px-1 flex flex-col items-center space-y-1 rounded-lg transition-all duration-300 hover:bg-gray-50 active:scale-95"
                  onClick={() => handleSubjectClick(subject)}
                >
                  <div className={`${subject.iconBgColor} rounded-lg p-2 shadow-sm`}>
                    <IconComponent className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-xs font-medium text-gray-700 text-center leading-tight">
                    {subject.name}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default SubjectsSidebar;